import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ProjectContext = createContext(null);

const STORAGE_KEY = "epm_selected_project_id";

export function ProjectProvider({ children }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    if (!user) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      setSelectedId((prev) => {
        if (prev && data.some((p) => p.id === prev)) return prev;
        const next = data[0]?.id || null;
        if (next) localStorage.setItem(STORAGE_KEY, next);
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const selectProject = (id) => {
    setSelectedId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
  };

  const selected = projects.find((p) => p.id === selectedId) || null;

  const myPermissions = (() => {
    if (!selected) return [];
    if (user?.role === "owner") return ["accounts", "resources", "tasks", "settings"];
    return selected.my_permissions || [];
  })();

  return (
    <ProjectContext.Provider
      value={{ projects, selected, selectedId, selectProject, loadProjects, loading, myPermissions }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export const useProject = () => useContext(ProjectContext);
